import {
  AUDIO_MAX_DURATION_MS,
  AUDIO_MIN_DURATION_MS,
  AUDIO_SUPPORTED_FORMATS,
  AUDIO_MIME_TYPE,
  AUDIO_EMPTY_RECORDING,
  AUDIO_DURATION_EXCEEDED,
  AUDIO_RECORDING_NOT_SUPPORTED
} from '@/assets/constants/constants'

/**
 * Audio utility service for recording, encoding, and validation
 */
const audioService = {
  /**
   * Check if the browser supports audio recording
   * @returns {boolean} true if MediaRecorder API is available
   */
  isRecordingSupported() {
    return !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.MediaRecorder
    )
  },

  /**
   * Request microphone access
   * @returns {Promise<MediaStream>} User's audio stream
   * @throws {Error} If user denies permission or microphone unavailable
   */
  async requestMicrophoneAccess() {
    try {
      if (!this.isRecordingSupported()) {
        throw new Error(AUDIO_RECORDING_NOT_SUPPORTED)
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      return stream
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        throw new Error('Microphone permission denied. Please enable it in your browser settings.')
      }
      throw error
    }
  },

  /**
   * Start recording audio from a MediaStream
   * @param {MediaStream} stream - Audio stream from getUserMedia
   * @returns {MediaRecorder} Configured MediaRecorder instance
   */
  createRecorder(stream) {
    const options = {
      mimeType: AUDIO_MIME_TYPE,
      audioBitsPerSecond: 128000
    }

    // Fallback if WebM not supported
    if (!MediaRecorder.isTypeSupported(AUDIO_MIME_TYPE)) {
      delete options.mimeType
    }

    const recorder = new MediaRecorder(stream, options)
    return recorder
  },

  /**
   * Convert audio Blob to Base64 string
   * @param {Blob} blob - Audio blob to encode
   * @returns {Promise<string>} Base64-encoded audio data
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1] // Remove data URL prefix
        resolve(base64String)
      }
      reader.onerror = () => {
        reject(new Error('Failed to read audio blob'))
      }
      reader.readAsDataURL(blob)
    })
  },

  /**
   * Convert Base64 string back to Blob
   * @param {string} base64Data - Base64-encoded audio data
   * @param {string} mimeType - MIME type of audio
   * @returns {Blob} Audio blob
   */
  base64ToBlob(base64Data, mimeType = AUDIO_MIME_TYPE) {
    const binaryString = atob(base64Data)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    return new Blob([bytes], { type: mimeType })
  },

  /**
   * Validate audio recording duration
   * @param {number} durationMs - Duration in milliseconds
   * @returns {Object} { valid: boolean, error?: string }
   */
  validateDuration(durationMs) {
    if (durationMs < AUDIO_MIN_DURATION_MS) {
      return {
        valid: false,
        error: AUDIO_EMPTY_RECORDING
      }
    }

    if (durationMs > AUDIO_MAX_DURATION_MS) {
      return {
        valid: false,
        error: AUDIO_DURATION_EXCEEDED
      }
    }

    return { valid: true }
  },

  /**
   * Validate audio blob
   * @param {Blob} blob - Audio blob to validate
   * @returns {Object} { valid: boolean, error?: string }
   */
  validateAudioBlob(blob) {
    if (!blob || blob.size === 0) {
      return {
        valid: false,
        error: AUDIO_EMPTY_RECORDING
      }
    }

    // Check if MIME type is supported (if available)
    // Extract base MIME type (before semicolon for codec info)
    if (blob.type) {
      const baseMimeType = blob.type.split(';')[0]
      if (!AUDIO_SUPPORTED_FORMATS.includes(baseMimeType)) {
        return {
          valid: false,
          error: `Unsupported audio format: ${blob.type}`
        }
      }
    }

    return { valid: true }
  },

  /**
   * Calculate audio duration from Blob (basic estimation)
   * For accurate duration, use Web Audio API to decode audio
   * @param {Blob} blob - Audio blob
   * @returns {Promise<number>} Duration in milliseconds (estimated)
   */
  async getAudioDuration(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob)
      const audio = new Audio()

      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url)
        resolve(Math.round(audio.duration * 1000)) // Convert to milliseconds
      }

      audio.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('Failed to load audio metadata'))
      }

      audio.src = url
    })
  },

  /**
   * Create a playable URL from audio Blob (for preview/playback)
   * @param {Blob} blob - Audio blob
   * @returns {string} Object URL for playback
   */
  createPlaybackUrl(blob) {
    return URL.createObjectURL(blob)
  },

  /**
   * Release a playback URL to free memory
   * @param {string} url - Object URL created by createPlaybackUrl
   */
  revokePlaybackUrl(url) {
    if (url && url.startsWith('blob:')) {
      URL.revokeObjectURL(url)
    }
  },

  /**
   * Format duration in milliseconds to human-readable string
   * @param {number} durationMs - Duration in milliseconds
   * @returns {string} Formatted string (e.g., "0:05" or "1:23")
   */
  formatDuration(durationMs) {
    if (!durationMs || durationMs < 0) return '0:00'

    const totalSeconds = Math.floor(durationMs / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  },

  /**
   * Parse duration from human-readable format back to milliseconds
   * @param {string} formatted - Formatted string (e.g., "1:23")
   * @returns {number} Duration in milliseconds
   */
  parseDuration(formatted) {
    const parts = formatted.split(':')
    const minutes = parseInt(parts[0]) || 0
    const seconds = parseInt(parts[1]) || 0
    return (minutes * 60 + seconds) * 1000
  },

  /**
   * Stop all tracks in a MediaStream
   * @param {MediaStream} stream - Media stream to stop
   */
  stopMediaStream(stream) {
    if (stream && stream.getTracks) {
      stream.getTracks().forEach(track => track.stop())
    }
  },

  /**
   * Check if a URL is a valid S3 presigned URL
   * @param {string} url - URL to check
   * @returns {boolean}
   */
  isValidPresignedUrl(url) {
    try {
      const urlObj = new URL(url)
      return (
        urlObj.hostname.includes('s3') ||
        urlObj.hostname.includes('amazonaws.com')
      ) && urlObj.searchParams.has('Signature')
    } catch {
      return false
    }
  }
}

export default audioService
